// Sendinvo subscription redirect. Triggered automatically by api() on a
// 402 SubscriptionRequired response. Instead of showing an intermediary
// modal, we send the user straight to the Stripe-powered billing flow:
// Checkout for new subscribers, the billing portal for accounts that need
// to fix an existing subscription.
import { API_BASE } from "../lib/api.js";
import { showAlertModal } from "./confirm.js";
import { subscriptionLapsedForOthers, subscriptionNeedsPortal } from "../lib/subscription-routing.js";
import { can } from "../lib/roles.js";

/**
 * Send the user straight to the Stripe billing flow.
 *
 * @param {object}  [opts]
 * @param {("none"|"trialing"|"active"|"past_due"|"unpaid"|"canceled"|string)} [opts.status="none"]
 *   "past_due"/"unpaid"/"canceled" → billing portal (POST /billing/portal),
 *   anything else → Checkout (POST /billing/checkout). Mirror of
 *   subscription.status on the server side.
 * @param {Window|null} [opts.targetWindow=null]
 *   If provided, the Stripe URL loads in this window instead of the current
 *   one. Callers open a blank tab synchronously inside the user's click (so
 *   the popup blocker allows it) and pass it here; we point it at Stripe once
 *   the URL is fetched. If null, we navigate the current tab (the default).
 * @returns {Promise<void>}
 */

export async function startSubscriptionRedirect({ status = "none", targetWindow = null } = {}) {
  // Billing is the COMPANY's, and only an owner or an admin may act on it.
  // A Member or Viewer used to be sent down this path anyway: the billing
  // route answered 403 and the alert read "You don't have permission to do
  // that" — about a subscription they were never told had lapsed, after
  // pressing Send on an invoice (issue #509). Tell them what is actually
  // wrong and who can fix it, and make no billing call at all.
  if (!can("billing.reenable")) {
    if (targetWindow) targetWindow.close();
    await showAlertModal({ title: subscriptionLapsedForOthers(status) });
    return;
  }
  const endpoint = subscriptionNeedsPortal(status) ? "/billing/portal" : "/billing/checkout";
  try {
    // Intentionally no body and no Content-Type: this endpoint reads only
    // request.session.user. Declaring application/json with an empty body
    // causes Fastify to reject the request with "Body cannot be empty…".
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: "POST",
      credentials: "include"
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data && data.url) {
      if (targetWindow) targetWindow.location.href = data.url;
      else window.location.href = data.url;
      return;
    }
    if (targetWindow) targetWindow.close();
    await showAlertModal({ title: data.message || data.error || "Couldn’t start checkout. Please try again." });
  } catch (err) {
    if (targetWindow) targetWindow.close();
    await showAlertModal({ title: err.message || "Couldn’t start checkout. Please try again." });
  }
}
